import React, { useEffect, useState } from 'react';
import { XIcon, StarIcon, ExternalLinkIcon, BookOpenIcon, FlameIcon, MoonIcon, EditIcon } from 'lucide-react';
import { formatIcons } from './constants';
import { WriteReviewModal, ReviewDraft } from '../../components/Reviews/WriteReviewModal';
interface PersonalBookData {
  userRating: number;
  status: string;
  dateRead: string;
  notes: string;
  formatOwned: string;
  review?: ReviewDraft;
}
interface BookDetailModalProps {
  book: any;
  darkMode: boolean;
  personalData: PersonalBookData;
  onSave: (bookId: string, data: PersonalBookData) => void;
  onClose: () => void;
  onNavigate: (id: string) => void;
}
const STATUS_OPTIONS = [{
  value: 'read',
  label: 'Read'
}, {
  value: 'currentlyReading',
  label: 'Currently Reading'
}, {
  value: 'tbr',
  label: 'To Be Read'
}, {
  value: 'dnf',
  label: 'Did Not Finish'
}];
const FORMAT_OPTIONS = [{
  value: 'paperback',
  label: 'Paperback'
}, {
  value: 'hardcover',
  label: 'Hardcover'
}, {
  value: 'ebook',
  label: 'eBook'
}, {
  value: 'audiobook',
  label: 'Audiobook'
}];
export const BookDetailModal: React.FC<BookDetailModalProps> = ({
  book,
  darkMode,
  personalData,
  onSave,
  onClose,
  onNavigate
}) => {
  const [form, setForm] = useState<PersonalBookData>({
    userRating: personalData.userRating || 0,
    status: personalData.status || book.status || 'tbr',
    dateRead: personalData.dateRead || '',
    notes: personalData.notes || '',
    formatOwned: personalData.formatOwned || book.format || 'paperback',
    review: personalData.review
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [saved, setSaved] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);
  const handleSave = () => {
    onSave(book.id, form);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };
  const spiceLevel = book.spiceLevel ?? 0;
  const darknessLevel = book.darknessLevel ?? 0;
  const bg = darkMode ? 'bg-gray-900' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400';
  const labelClass = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-10691" data-mp="252n8ocrko4fz">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-10692" data-mp="fljcesupcpen" />

      {/* Modal */}
      <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl border ${bg} ${border} overflow-hidden`} style={{
      maxHeight: '90vh'
    }} data-id="element-10693" data-mp="phw5ppmc518v">
        {/* Header */}
        <div className={`flex items-start gap-4 p-5 border-b ${border}`} data-id="element-10694" data-mp="3syv311esem5a">
          {/* Cover */}
          <div className="flex-shrink-0 w-16 h-24 rounded-lg overflow-hidden shadow-md" data-id="element-10695" data-mp="1rineqtjygrh">
            <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" data-id="element-10696" data-mp="5tvov319nsig" />
          </div>
          {/* Title / author */}
          <div className="flex-1 min-w-0 pt-1" data-id="element-10697" data-mp="3a83l3bgqhnfa">
            <h2 className={`font-bold text-lg leading-tight line-clamp-2 ${textPrimary}`} data-id="element-10698" data-mp="3a7c1kz0evg5d">
              {book.title}
            </h2>
            <p className={`text-sm mt-0.5 ${textSecondary}`} data-id="element-10699" data-mp="2v4ajk0o4d5ah">
              by {book.author}
            </p>
            {(book.series || book.seriesName) && <p className="text-xs text-indigo-500 mt-1" data-id="element-10700" data-mp="2q3wcakucdzdi">
                {book.series || book.seriesName}
                {book.seriesNumber ? ` #${book.seriesNumber}` : ''}
              </p>}
            {/* Spice / darkness inline */}
            <div className="flex items-center gap-3 mt-2" data-id="element-10701" data-mp="3dua7ulc4ygcu">
              {spiceLevel > 0 && <div className="flex gap-0.5" title={`Spice: ${spiceLevel}/5`} data-id="element-10702" data-mp="38v718wgic22r">
                  {Array.from({
                length: 5
              }).map((_, i) => <FlameIcon key={i} className={`h-3 w-3 ${i < spiceLevel ? 'text-red-400 fill-red-400' : darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-10703" data-mp="3itv8r1av8ys7" data-mpi={i} />)}
                </div>}
              {darknessLevel > 0 && <div className="flex gap-0.5" title={`Darkness: ${darknessLevel}/5`} data-id="element-10704" data-mp="3ze2bbj7d1hu">
                  {Array.from({
                length: 5
              }).map((_, i) => <MoonIcon key={i} className={`h-3 w-3 ${i < darknessLevel ? 'text-indigo-400 fill-indigo-400' : darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-10705" data-mp="3qbtcsqaa3ho1" data-mpi={i} />)}
                </div>}
            </div>
          </div>
          {/* Close */}
          <button onClick={onClose} className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-10706" data-mp="37fuvz5mnx5wb">
            <XIcon className="h-5 w-5" data-id="element-10707" data-mp="15ozxg612mslw" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto p-5 space-y-5" style={{
        maxHeight: 'calc(90vh - 200px)'
      }} data-id="element-10708" data-mp="1algnx6vqtj25">
          {/* My Rating */}
          <div data-id="element-10709" data-mp="1469lwvvfz32q">
            <label className={labelClass} data-id="element-10710" data-mp="145q89rctr0ui">My Rating</label>
            <div className="flex gap-1" data-id="element-10711" data-mp="z76gvbfgowur">
              {[1, 2, 3, 4, 5].map(star => <button key={star} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} onClick={() => setForm(f => ({
              ...f,
              userRating: star === f.userRating ? 0 : star
            }))} className="p-0.5 transition-transform hover:scale-110" data-id="element-10712" data-mp="u8d79u3appzv">
                  <StarIcon className={`h-7 w-7 transition-colors ${(hoverRating || form.userRating) >= star ? 'text-amber-400 fill-amber-400' : darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-10713" data-mp="1d54wsl9fl1cd" />
                </button>)}
              {form.userRating > 0 && <span className={`ml-2 self-center text-sm font-medium ${textSecondary}`} data-id="element-10714" data-mp="2e6vjgr66lwp2">
                  {['', "Didn't like it", 'It was ok', 'Liked it', 'Really liked it', 'It was amazing'][form.userRating]}
                </span>}
            </div>
          </div>

          {/* Reading Status */}
          <div data-id="element-10715" data-mp="35wqd3012o8jj">
            <label className={labelClass} data-id="element-10716" data-mp="35w8i06q5c0d7">Reading Status</label>
            <div className="grid grid-cols-2 gap-2" data-id="element-10717" data-mp="3av1lr7fmsd06">
              {STATUS_OPTIONS.map(opt => <button key={opt.value} onClick={() => setForm(f => ({
              ...f,
              status: opt.value
            }))} className={`px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors border ${form.status === opt.value ? 'bg-indigo-600 border-indigo-600 text-white' : darkMode ? 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'}`} data-id="element-10718" data-mp="3funzrj3a9xa1">
                  {opt.label}
                </button>)}
            </div>
          </div>

          {/* Format Owned */}
          <div data-id="element-10719" data-mp="3ifmvgxxkzioz">
            <label className={labelClass} data-id="element-10720" data-mp="3ig6fck6skh5n">Format Owned</label>
            <div className="flex flex-wrap gap-2" data-id="element-10721" data-mp="m2dy4caglv">
              {FORMAT_OPTIONS.map(opt => <button key={opt.value} onClick={() => setForm(f => ({
              ...f,
              formatOwned: opt.value
            }))} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${form.formatOwned === opt.value ? 'bg-indigo-600 border-indigo-600 text-white' : darkMode ? 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'}`} data-id="element-10722" data-mp="3rersu0xep5zk">
                  <span className="opacity-80" data-id="element-10723" data-mp="1a7i5p5id10bh">{formatIcons[opt.value]}</span>
                  {opt.label}
                </button>)}
            </div>
          </div>

          {/* Date Read */}
          <div data-id="element-10724" data-mp="21xctuyuhejyk">
            <label className={labelClass} data-id="element-10725" data-mp="21vs920xyyfdw">Date Read</label>
            <input type="date" value={form.dateRead} onChange={e => setForm(f => ({
            ...f,
            dateRead: e.target.value
          }))} className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${inputBg}`} data-id="element-10726" data-mp="1nx21tshfqzbt" />
          </div>

          {/* My Review */}
          <div data-id="element-10727" data-mp="36efen7pxc2j6">
            <label className={labelClass} data-id="element-10728" data-mp="3bbflvlyifbbv">My Review</label>
            {form.review ? <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-10729" data-mp="3bd8jycv24n89">
                <div className="flex items-center justify-between mb-2" data-id="element-10730" data-mp="31k1jn6f6bxf1">
                  <div className="flex text-amber-400" data-id="element-10731" data-mp="2nll914e6xmrm">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < form.review!.rating ? 'fill-current' : 'text-gray-300 dark:text-gray-600'}`} data-id="element-10732" data-mp="2ro7wkt3twlj0" data-mpi={i} />)}
                  </div>
                  <button onClick={() => setIsReviewModalOpen(true)} className="flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300" data-id="element-10733" data-mp="bwk0t10jwz1s">
                    <EditIcon className="h-3 w-3 mr-1" data-id="element-10734" data-mp="21dpfmw544850" /> Edit Review
                  </button>
                </div>
                <p className={`text-sm line-clamp-3 ${textSecondary}`} data-id="element-10735" data-mp="1wci5iqfhywv6">
                  {form.review.review}
                </p>
              </div> : <button onClick={() => setIsReviewModalOpen(true)} className={`w-full py-3 rounded-lg border border-dashed text-sm font-medium transition-colors ${darkMode ? 'border-gray-600 text-gray-400 hover:bg-gray-800 hover:text-gray-300' : 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-10736" data-mp="26axy6xu6ae6s">
                + Write a Review
              </button>}
          </div>

          {/* Personal Notes */}
          <div data-id="element-10737" data-mp="zp3oos5xkxwm">
            <label className={labelClass} data-id="element-10738" data-mp="zolv63ankxse">My Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({
            ...f,
            notes: e.target.value
          }))} placeholder="Your thoughts, favourite quotes, things to remember..." rows={4} className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none ${inputBg}`} data-id="element-10739" data-mp="upioyfixsliu" />
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between gap-3 p-4 border-t ${border}`} data-id="element-10740" data-mp="23bh7efbp8bu0">
          <button onClick={() => {
          onNavigate(book.id);
          onClose();
        }} className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`} data-id="element-10741" data-mp="27brbfmfxks68">
            <BookOpenIcon className="h-4 w-4" data-id="element-10742" data-mp="2m0nmzth74nbl" />
            View Book Page
            <ExternalLinkIcon className="h-3 w-3" data-id="element-10743" data-mp="2r1tclifk7ugw" />
          </button>
          <button onClick={handleSave} className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${saved ? 'bg-green-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`} data-id="element-10744" data-mp="ff9cnpkkns46">
            {saved ? '✓ Saved' : 'Save'}
          </button>
        </div>
      </div>

      <WriteReviewModal isOpen={isReviewModalOpen} onClose={() => setIsReviewModalOpen(false)} existingReview={form.review} subject={{
      id: book.id,
      type: 'book',
      title: book.title,
      subtitle: book.author,
      image: book.coverImage
    }} onSave={draft => {
      setForm(f => ({
        ...f,
        review: draft,
        userRating: draft.rating
      }));
      setIsReviewModalOpen(false);
    }} zIndex={60} data-id="element-10745" data-mp="iie1iiq2kfm" />
    </div>;
};